import type * as THREE from 'three'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import { BackendMismatchError } from '@/graphics/GraphicsErrors'
import { DEFAULT_MAX_NODES_LOADING, DEFAULT_POINT_BUDGET } from '@/shared/constants'
import type { DisposableScope } from '@/shared/disposable/DisposableScope'
import { getLoggerManager, type ILogger } from '@/shared/logging'
import type { Disposable } from '@/shared/types/core'
import type { ServiceMap } from '@/shared/types/services'
import type { UnitPreference } from '@/shared/unitPresentation'
import type { EventBus } from './EventBus'
import { GltfExtensionRegistry } from './GltfExtensionRegistry'
import { RenderInvalidationEventBridge } from './RenderInvalidationEventBridge'
import { RenderInvalidationService } from './RenderInvalidationService'
import { SceneBoundsService } from './SceneBoundsService'
import type { SceneGraphService } from './SceneGraphService'
import { UnitPresentationService } from './UnitPresentationService'

let nextMemoryDiagnosticContextId = 1

/**
 * 引擎上下文 —— 基础设施持有者 + 服务注册中心
 *
 * 只持有全局稳定资源和服务注册表。
 * 禁止放入：业务 Plugin 实例、资产实例、控制器/工具状态、可变的业务对象引用
 */
export class EngineContext implements Disposable {
  /** 跨 runtime/plugin 日志关联同一引擎上下文的诊断 ID。 */
  readonly memoryDiagnosticId = nextMemoryDiagnosticContextId++
  // ===== 基础设施（引擎存活期间不可变） =====
  private _container: HTMLElement | null
  private _renderer: THREE.WebGLRenderer | null
  private _graphics: GraphicsBackend | null
  /** 引擎全局事件总线 */
  readonly events: EventBus
  /** 场景图管理服务 */
  readonly sceneGraph: SceneGraphService
  /** 场景包围盒聚合服务 */
  readonly sceneBounds: SceneBoundsService
  /** 生命周期统一托管作用域，dispose 时按 LIFO 释放 */
  readonly disposables: DisposableScope
  /** THREE 资源加载管理器，已桥接到 EventBus */
  readonly loadingManager: THREE.LoadingManager
  /** 统一日志 root logger，插件用 logger.child(ns) 派生命名空间实例 */
  readonly logger: ILogger
  /** 按需渲染失效服务 */
  readonly renderInvalidation: RenderInvalidationService
  /** 语义事件到渲染 dirty 的桥接 */
  readonly renderInvalidationEventBridge: RenderInvalidationEventBridge
  /** 实例级显示单位服务 */
  readonly unitPresentation: UnitPresentationService
  /** glTF 容器 Loader 与资产插件之间的实例级扩展注入边界。 */
  readonly gltfExtensions: GltfExtensionRegistry

  // ===== 全局配置（可变） =====
  /** 点云全局点预算上限 */
  pointBudget: number
  /** 点云节点并发加载上限 */
  maxNodesLoading: number

  // ===== 帧级计数（由 RenderLoop 维护，权威帧号） =====
  /** 当前帧号，由 RenderLoop 在每帧开始时递增 */
  frameNumber: number = 0

  // ===== 服务注册与发现 =====
  private services = new Map<string, unknown>()

  /** 当前已注册服务数量，仅用于生命周期诊断。 */
  get serviceCount(): number {
    return this.services.size
  }

  /** 渲染画布挂载的宿主容器；引擎销毁后访问会抛错。 */
  get container(): HTMLElement {
    if (!this._container) {
      throw new Error('EngineContext 已释放宿主容器引用')
    }
    return this._container
  }

  /** 引擎共享 WebGLRenderer；引擎销毁后访问会抛错。 */
  get renderer(): THREE.WebGLRenderer {
    if (this._graphics?.kind === 'webgpu') {
      throw new BackendMismatchError('webgl2', this._graphics.kind, 'EngineContext.renderer')
    }
    if (!this._renderer) {
      throw new Error('EngineContext 已释放 WebGLRenderer 引用')
    }
    return this._renderer
  }

  /** 引擎唯一权威图形后端；宿主引用释放后访问会抛错。 */
  get graphics(): GraphicsBackend {
    if (!this._graphics) {
      throw new Error('EngineContext 已释放 GraphicsBackend 引用')
    }
    return this._graphics
  }

  constructor(params: {
    container: HTMLElement
    graphics: GraphicsBackend
    renderer?: THREE.WebGLRenderer
    events: EventBus
    sceneGraph: SceneGraphService
    sceneBounds?: SceneBoundsService
    disposables: DisposableScope
    loadingManager: THREE.LoadingManager
    logger?: ILogger
    pointBudget?: number
    maxNodesLoading?: number
    unitPreference?: Partial<UnitPreference>
  }) {
    this._container = params.container
    this._graphics = params.graphics
    this._renderer = params.renderer ?? null
    if (params.graphics.kind === 'webgl2' && !this._renderer) {
      throw new BackendMismatchError('webgl2', params.graphics.kind, 'EngineContext renderer')
    }
    this.events = params.events
    this.sceneGraph = params.sceneGraph
    this.sceneBounds = params.sceneBounds ?? new SceneBoundsService()
    this.disposables = params.disposables
    this.loadingManager = params.loadingManager
    this.logger = params.logger ?? getLoggerManager().root
    this.pointBudget = params.pointBudget ?? DEFAULT_POINT_BUDGET
    this.maxNodesLoading = params.maxNodesLoading ?? DEFAULT_MAX_NODES_LOADING
    this.renderInvalidation = this.disposables.track(new RenderInvalidationService())
    this.renderInvalidationEventBridge = this.disposables.track(
      new RenderInvalidationEventBridge(this.events, this.renderInvalidation)
    )
    this.unitPresentation = this.disposables.track(
      new UnitPresentationService(params.unitPreference)
    )
    this.gltfExtensions = this.disposables.track(new GltfExtensionRegistry())
    this.registerService('Logger', this.logger)
    this.registerService('RenderInvalidationService', this.renderInvalidation)
    this.registerService('UnitPresentationService', this.unitPresentation)
    this.registerService('GltfExtensionRegistry', this.gltfExtensions)
  }

  /**
   * 注册服务，Plugin 在 onInit 中调用。
   *
   * 重复注册同一 key 抛错，拒绝 nullish 值。
   *
   * @param name - 服务键名
   * @param service - 服务实例，不得为 null/undefined
   */
  registerService<K extends keyof ServiceMap>(name: K, service: ServiceMap[K]): void {
    if (service == null) {
      throw new Error(`服务 "${String(name)}" 的值不能为 null 或 undefined`)
    }
    if (this.services.has(name as string)) {
      throw new Error(`服务 "${String(name)}" 已被注册，禁止重复注册`)
    }
    this.services.set(name as string, service)
  }

  /**
   * 获取服务，Plugin 在 onStart 及之后调用。
   *
   * @param name - 服务键名
   * @returns 对应服务实例；未注册时抛明确错误
   */
  getService<K extends keyof ServiceMap>(name: K): ServiceMap[K] {
    if (!this.services.has(name as string)) {
      throw new Error(`服务 "${String(name)}" 未注册，请检查 Plugin 依赖配置`)
    }
    return this.services.get(name as string) as ServiceMap[K]
  }

  /**
   * 查询服务是否已注册。
   *
   * @param name - 服务键名
   * @returns 已注册返回 true，否则 false
   */
  hasService<K extends keyof ServiceMap>(name: K): boolean {
    return this.services.has(name as string)
  }

  /**
   * 获取容器当前尺寸。
   *
   * @returns 宿主容器宽高，单位 CSS 像素
   */
  getSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    }
  }

  /**
   * 获取设备像素比。
   *
   * @returns 当前渲染器设备像素比
   */
  getPixelRatio(): number {
    return this.graphics.surface.pixelRatio
  }

  /** 返回尚未释放的 Renderer，仅供销毁流程和生命周期诊断使用。 */
  getRendererIfAvailable(): THREE.WebGLRenderer | null {
    return this._graphics?.kind === 'webgl2' ? this._renderer : null
  }

  /** 返回尚未释放的 GraphicsBackend，仅供销毁流程和生命周期诊断使用。 */
  getGraphicsIfAvailable(): GraphicsBackend | null {
    return this._graphics
  }

  /** 在 Renderer 完成 dispose 并移除 Canvas 后断开宿主对象引用。 */
  releaseHostReferences(): void {
    this._renderer = null
    this._graphics = null
    this._container = null
  }

  /** 释放 context 内部资源（EventBus / WebGLRenderer 的最终释放由 PotreeEngine.dispose() 统一负责） */
  dispose(): void {
    this.disposables.dispose()
    this.sceneBounds.dispose()
    this.sceneGraph.dispose()
    this.services.clear()
  }
}
